import { Dropdown as FuseDropdown } from '@tvg/fuse-design/dropdown';
import { gridConfig } from '@tvg/fuse-design/responsive-grid';
import { PageType } from '@tvg/product-listing-url';
import { PLPQueryData, usePLPQuery } from 'client/api/plp-results';
import { EUserAction } from 'client/config';
import { usePLPNavigate } from 'client/hooks/usePLPNavigate';
import useGlobalConfig from 'client/state/GlobalConfig';
import { DEFAULT_SORT_OPTION } from 'common/config';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

const FuseDropdownStyled = styled(FuseDropdown)`
  .fuse-dropdown__wrapper {
    border: none;
  }

  &.fuse-dropdown {
    display: none;

    min-height: 48px;

    border-radius: var(--spacing-space4);
    border: var(--border-width-xs) solid var(--grey-grey-700, #878787);
    background: var(--base-white, #fff);

    select {
      &:focus {
        outline: 0px;
      }
      > option:first-child {
        display: none; /* remove dublicated option */
      }
    }
  }
`;

const FuseDropdownTablet = styled(FuseDropdownStyled)`
  &.fuse-dropdown {
    @media screen and (max-width: ${gridConfig.m.maxWidth}) {
      display: flex;
    }
  }
`;

const FuseDropdownDesktopOnly = styled(FuseDropdownStyled)`
  &.fuse-dropdown {
    @media screen and (min-width: ${gridConfig.l.minWidth}) {
      display: flex;
    }
  }
`;

// const { useState } = React;

export type SortDropdownProps = {
  showInMobile?: boolean;
};

enum sortValue {
  relevance = 'relevance',
  brand = 'brand',
  highestrated = 'highestrated',
  newin = 'newin',
  pricedesc = 'pricedesc',
  price = 'price',
}

export const mapSortOptions: Record<string, [string, string]> = {
  [sortValue.relevance]: ['relevance', 'descending'],
  [sortValue.brand]: ['brand_name', 'ascending'],
  [sortValue.highestrated]: ['rating', 'descending'],
  [sortValue.newin]: ['new_in', 'descending'],
  [sortValue.pricedesc]: ['price', 'descending'],
  [sortValue.price]: ['price', 'ascending'],
};

function SortOrdersDropdown({ showInMobile }: SortDropdownProps) {
  const { pageType } = useGlobalConfig();
  const plpNavigate = usePLPNavigate();
  const { data: selectedValue } = usePLPQuery(useCallback((d: PLPQueryData) => d.sortBy || DEFAULT_SORT_OPTION, []));

  const optionsDropDown = useMemo(
    () => [
      {
        label: pageType === PageType.Search ? 'Most Relevant' : 'Recommended',
        value: sortValue.relevance,
      },
      {
        label: 'New In',
        value: sortValue.newin,
      },
      {
        label: 'Price Low to High',
        value: sortValue.price,
      },
      {
        label: 'Price High to Low',
        value: sortValue.pricedesc,
      },
      {
        label: 'Highest Rated',
        value: sortValue.highestrated,
      },
      {
        label: 'Brand A - Z',
        value: sortValue.brand,
      },
    ],
    [pageType],
  );

  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e?.currentTarget.value;
      plpNavigate({ query: { sortOption: value } }, EUserAction.SORT_ORDER);
    },
    [plpNavigate],
  );

  if (showInMobile) {
    return (
      <FuseDropdownTablet
        className="fuse-dropdown"
        dataTestId="fuse-dropdown--tablet"
        componentId="fuse-dropdown--tablet"
        helperText=""
        label=""
        options={optionsDropDown}
        value={selectedValue ?? DEFAULT_SORT_OPTION}
        selectId="fuse-dropdown-container--sort-order-mobile"
        size="large"
        state="default"
        onChange={handleSortChange}
        aria-label="Sort order"
      />
    );
  }
  return (
    <FuseDropdownDesktopOnly
      className="fuse-dropdown"
      dataTestId="fuse-dropdown--desktop"
      componentId="fuse-dropdown--desktop"
      helperText=""
      label=""
      options={optionsDropDown}
      value={selectedValue}
      selectId="fuse-dropdown-container--sort-order-desktop"
      size="large"
      state="default"
      onChange={handleSortChange}
      aria-label="Sort order"
    />
  );
}

export default React.memo(SortOrdersDropdown);
